import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import './SeasonalEffects.css';

export const SETTINGS_UPDATED_EVENT = 'site-settings-updated';

const HIDDEN_ROUTE_PREFIXES = ['/watch', '/watch-series'];
const BULB_SPACING = 48;
const BULB_COLORS = ['#ff3b3b', '#ffd23b', '#3bff6a', '#3bb4ff', '#ff6bd5'];

const rand = (min, max) => Math.random() * (max - min) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ---------- Particle factories ----------

const createSnowflake = (w, h, initial) => {
  const glyph = Math.random() < 0.08;
  return {
    type: 'snow',
    x: rand(0, w),
    y: initial ? rand(-h, h) : rand(-40, -10),
    r: glyph ? rand(9, 15) : rand(1, 3.4),
    glyph,
    speed: rand(0.4, 1.4),
    drift: rand(0.3, 1.2),
    phase: rand(0, Math.PI * 2),
    alpha: rand(0.4, 0.9),
    rot: rand(0, Math.PI * 2),
    rotSpeed: rand(-0.01, 0.01)
  };
};

const createBat = (w, h) => {
  const fromLeft = Math.random() < 0.5;
  return {
    type: 'bat',
    x: fromLeft ? -40 : w + 40,
    y: rand(h * 0.08, h * 0.6),
    baseY: 0,
    vx: (fromLeft ? 1 : -1) * rand(1.2, 2.6),
    scale: rand(0.8, 1.6),
    flap: rand(0.25, 0.4),
    phase: rand(0, Math.PI * 2),
    wave: rand(15, 45)
  };
};

const createLeaf = (w, h, initial) => ({
  type: 'leaf',
  x: rand(0, w),
  y: initial ? rand(-h, h) : rand(-40, -10),
  size: rand(5, 9),
  speed: rand(0.6, 1.4),
  drift: rand(0.6, 1.6),
  phase: rand(0, Math.PI * 2),
  rot: rand(0, Math.PI * 2),
  rotSpeed: rand(-0.04, 0.04),
  color: pick(['#d9631e', '#b3441b', '#e8901f', '#8a3b12'])
});

const createPumpkin = (w, h, initial) => ({
  type: 'pumpkin',
  x: rand(0, w),
  y: initial ? rand(-h, h) : rand(-60, -30),
  size: rand(16, 26),
  speed: rand(0.4, 0.9),
  drift: rand(0.4, 1),
  phase: rand(0, Math.PI * 2),
  rot: rand(-0.3, 0.3),
  rotSpeed: rand(-0.01, 0.01),
  glyph: Math.random() < 0.3 ? '👻' : '🎃'
});

// ---------- Drawing ----------

const drawBat = (ctx, b, t) => {
  const f = Math.sin(t * b.flap + b.phase);
  const dir = b.vx > 0 ? 1 : -1;
  ctx.save();
  ctx.translate(b.x, b.y);
  ctx.scale(b.scale * dir, b.scale);
  ctx.fillStyle = 'rgba(25, 12, 30, 0.95)';
  ctx.shadowColor = 'rgba(255, 130, 30, 0.75)';
  ctx.shadowBlur = 8;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(-6, -6 * f - 3, -15, -7 * f);
  ctx.quadraticCurveTo(-12, -1, -10, 2);
  ctx.quadraticCurveTo(-7, 0, -5, 3);
  ctx.quadraticCurveTo(-3, 1, 0, 4);
  ctx.quadraticCurveTo(3, 1, 5, 3);
  ctx.quadraticCurveTo(7, 0, 10, 2);
  ctx.quadraticCurveTo(12, -1, 15, -7 * f);
  ctx.quadraticCurveTo(6, -6 * f - 3, 0, 0);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(0, 1, 2.6, 3.6, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

const drawLeaf = (ctx, l) => {
  ctx.save();
  ctx.translate(l.x, l.y);
  ctx.rotate(l.rot);
  ctx.globalAlpha = 0.75;
  ctx.fillStyle = l.color;
  ctx.beginPath();
  ctx.ellipse(0, 0, l.size, l.size * 0.45, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(60, 20, 5, 0.6)';
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(-l.size, 0);
  ctx.lineTo(l.size, 0);
  ctx.stroke();
  ctx.restore();
};

const drawGlyph = (ctx, p, glyph, size, alpha) => {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rot);
  ctx.globalAlpha = alpha;
  ctx.font = `${size}px serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(glyph, 0, 0);
  ctx.restore();
};

const drawSnow = (ctx, s) => {
  if (s.glyph) {
    ctx.save();
    ctx.fillStyle = '#ffffff';
    drawGlyph(ctx, s, '❄', s.r * 1.6, s.alpha * 0.8);
    ctx.restore();
    return;
  }
  ctx.globalAlpha = s.alpha;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
};

// ---------- Canvas engine ----------

const ParticleCanvas = ({ halloween, christmas }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let w = 0;
    let h = 0;
    let raf = null;
    let t = 0;
    let particles = [];

    const counts = () => {
      const area = Math.min(1, (w * h) / (1920 * 1080));
      return {
        snow: christmas ? Math.round(140 * area) + 30 : 0,
        leaf: halloween ? Math.round(18 * area) + 6 : 0,
        pumpkin: halloween ? Math.round(6 * area) + 2 : 0,
        bat: halloween ? 4 : 0
      };
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const seed = () => {
      const c = counts();
      particles = [];
      for (let i = 0; i < c.snow; i++) particles.push(createSnowflake(w, h, true));
      for (let i = 0; i < c.leaf; i++) particles.push(createLeaf(w, h, true));
      for (let i = 0; i < c.pumpkin; i++) particles.push(createPumpkin(w, h, true));
      for (let i = 0; i < c.bat; i++) {
        const bat = createBat(w, h);
        bat.x = rand(0, w);
        bat.baseY = bat.y;
        particles.push(bat);
      }
    };

    const step = () => {
      t += 1;
      ctx.clearRect(0, 0, w, h);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        if (p.type === 'bat') {
          if (!p.baseY) p.baseY = p.y;
          p.x += p.vx;
          p.y = p.baseY + Math.sin(t * 0.02 + p.phase) * p.wave;
          if (p.x < -60 || p.x > w + 60) {
            particles[i] = createBat(w, h);
            particles[i].baseY = particles[i].y;
          }
          drawBat(ctx, p, t);
          continue;
        }

        p.y += p.speed;
        p.x += Math.sin(t * 0.01 + p.phase) * p.drift * 0.5;
        if (p.rotSpeed) p.rot += p.rotSpeed;

        if (p.y > h + 40) {
          if (p.type === 'snow') particles[i] = createSnowflake(w, h, false);
          else if (p.type === 'leaf') particles[i] = createLeaf(w, h, false);
          else particles[i] = createPumpkin(w, h, false);
          continue;
        }
        if (p.x < -40) p.x = w + 20;
        if (p.x > w + 40) p.x = -20;

        if (p.type === 'snow') drawSnow(ctx, p);
        else if (p.type === 'leaf') drawLeaf(ctx, p);
        else drawGlyph(ctx, p, p.glyph, p.size, 0.8);
      }

      raf = requestAnimationFrame(step);
    };

    const start = () => {
      if (raf === null) raf = requestAnimationFrame(step);
    };
    const stop = () => {
      if (raf !== null) cancelAnimationFrame(raf);
      raf = null;
    };
    const onVisibility = () => (document.hidden ? stop() : start());
    const onResize = () => {
      resize();
      seed();
    };

    resize();
    seed();
    start();
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [halloween, christmas]);

  return <canvas ref={canvasRef} className="seasonal-canvas" aria-hidden="true" />;
};

// ---------- Static decorations ----------

const HalloweenDecor = () => (
  <>
    <div className="halloween-fog" />
    <svg className="halloween-web" viewBox="0 0 100 100" aria-hidden="true">
      <g stroke="#ffffff" strokeWidth="0.7" fill="none">
        <line x1="100" y1="0" x2="0" y2="0" />
        <line x1="100" y1="0" x2="15" y2="45" />
        <line x1="100" y1="0" x2="55" y2="85" />
        <line x1="100" y1="0" x2="100" y2="100" />
        <path d="M80 0 Q84 6 89 11 Q94 16 100 20" />
        <path d="M58 0 Q66 13 75 24 Q86 35 100 42" />
        <path d="M36 0 Q48 20 62 37 Q78 54 100 64" />
        <path d="M14 0 Q30 27 47 50 Q70 74 100 86" />
      </g>
    </svg>
    <div className="halloween-spider">
      <div className="halloween-spider__thread" />
      <div className="halloween-spider__body">🕷️</div>
    </div>
  </>
);

const ChristmasLights = () => {
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const count = Math.ceil(width / BULB_SPACING);

  return (
    <div className="xmas-lights" aria-hidden="true">
      <svg className="xmas-lights__wire" preserveAspectRatio="none">
        <defs>
          <pattern id="xmas-wire" width={BULB_SPACING} height="30" patternUnits="userSpaceOnUse">
            <path d={`M0 2 Q${BULB_SPACING / 2} 22 ${BULB_SPACING} 2`} stroke="#2f4a33" strokeWidth="2" fill="none" />
          </pattern>
        </defs>
        <rect width="100%" height="30" fill="url(#xmas-wire)" />
      </svg>
      {Array.from({ length: count }).map((_, i) => (
        <span
          key={i}
          className="xmas-bulb"
          style={{
            left: i * BULB_SPACING + BULB_SPACING / 2,
            '--bulb-color': BULB_COLORS[i % BULB_COLORS.length],
            animationDelay: `${(i % 4) * 0.4}s`
          }}
        />
      ))}
    </div>
  );
};

// ---------- Main component ----------

const SeasonalEffects = () => {
  const location = useLocation();
  const [themes, setThemes] = useState({ halloween: false, christmas: false });

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/settings')
      .then((res) => {
        if (!cancelled && res.data?.themes) setThemes(res.data.themes);
      })
      .catch(() => {});

    const onUpdated = (e) => {
      if (e.detail?.themes) setThemes(e.detail.themes);
    };
    window.addEventListener(SETTINGS_UPDATED_EVENT, onUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener(SETTINGS_UPDATED_EVENT, onUpdated);
    };
  }, []);

  const hiddenOnRoute = HIDDEN_ROUTE_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
  const { halloween, christmas } = themes;

  if (hiddenOnRoute || (!halloween && !christmas)) return null;

  return (
    <div className="seasonal-layer" aria-hidden="true">
      {!prefersReducedMotion() && <ParticleCanvas halloween={halloween} christmas={christmas} />}
      {halloween && <HalloweenDecor />}
      {christmas && (
        <>
          <div className="xmas-snowbank" />
          <ChristmasLights />
        </>
      )}
    </div>
  );
};

export default SeasonalEffects;
